import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAppActions, useAppState } from '../../overmind';
import { CompanyRoleListItemResponse } from '../../models/overmind/company-entity';
import { FetchType } from '../../enums';
import { useKeplerNavigate } from '../../navigation/guards/use-kepler-navigate';
import { DropdownSelect, PageHeader } from '@keplerco/core';
import { PageLoaderLayer } from '../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { EntitiesCardWidget } from '../../components/cards/entities-card.widget';
import YourCareerPeopleWidget from './people/your-career-people.widget';
import YourCareerSkillsWidget from './skills/your-career-skills.widget';
import styles from './your-career.module.css';
import { PagePath } from '../../navigation/navigation.enums';

function YourCareerPageHeader({ role }: { role: CompanyRoleListItemResponse | undefined }) {
  const keplerNavigate = useKeplerNavigate();

  const { user } = useAppState();

  if (!role || !user?.jobRoles.length) {
    return (
      <PageHeader
        title="Role"
        breadcrumbs={[
          { name: `Your Career`, url: PagePath.yourCareerBase },
          { name: `Role`, url: window.location.pathname },
        ]}
        divider
      />
    );
  }

  if (user.jobRoles.length < 2) {
    return (
      <PageHeader
        title={role.name}
        breadcrumbs={[
          { name: `Your Career`, url: PagePath.yourCareerBase },
          { name: `${role?.name}`, url: window.location.pathname },
        ]}
        divider
      />
    );
  }

  return (
    <PageHeader
      title={role.name}
      breadcrumbs={[
        { name: `Your Career`, url: PagePath.yourCareerBase },
        { name: `${role?.name}`, url: window.location.pathname },
      ]}
      type="actions"
      divider
    >
      <div className="headerActions">
        <DropdownSelect
          label="Select role"
          options={
            user?.jobRoles.map(jobRole => ({
              label: jobRole.name,
              value: jobRole.slug,
              onClick: () => keplerNavigate(`${PagePath.yourCareerBase}${PagePath.yourCareerRole.replace(':roleSlug', jobRole.slug)}`),
            })) ?? []
          }
          value={role.slug}
        />
      </div>
    </PageHeader>
  );
}

export function YourCareerPage(): JSX.Element {
  const { roleSlug } = useParams();

  const actions = useAppActions();

  const [role, setRole] = useState<CompanyRoleListItemResponse>();

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.PageFetching });
      const response = await actions.getUserCompanyRole({ roleSlug: roleSlug! });
      setRole(response);
      actions.stopLoader(PagePath.yourCareerRole);
    }

    getData();
  }, [roleSlug]);

  return (
    <PageLoaderLayer path={PagePath.yourCareerRole}>
      <div className="pageWrapper wrapper">
        <YourCareerPageHeader role={role} />

        {!!role && (
          <div className={styles.body}>
            <EntitiesCardWidget title="Role Description" description={role.description} />

            <YourCareerPeopleWidget role={role} />

            <YourCareerSkillsWidget role={role} />
          </div>
        )}
      </div>
    </PageLoaderLayer>
  );
}
